/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { defineMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import Image from '@atlaskit/image';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import teamWorkGraphIcons from './assets/team-work-graph-icons.svg';
import workProfileIconUrl from './assets/work-profile-icon.svg';

const styles = cssMap({
	container: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		backgroundPosition: '0px 0px, -4px -4px, -4px -4px',
		backgroundImage: `linear-gradient(to right, ${token('elevation.surface.sunken')}, transparent 40%), linear-gradient(${token('color.background.neutral')} 1px, transparent 1px), linear-gradient(90deg, ${token('color.background.neutral')} 1px, transparent 1px)`,
		backgroundSize: '100% 100%, 20px 20px, 20px 20px',
		width: '100%',
		boxSizing: 'border-box',
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	title: {
		font: token('font.heading.small'),
		color: token('color.text'),
	},
	description: {
		maxWidth: '70%',
	},
	buttonsContainer: {
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.small'),
	},
});

const workProfileTitle = defineMessage({
	id: 'ai-mate.rovo-memories.manage-memory-content.work-profile.title',
	defaultMessage: 'Work profile',
	description: 'Title for the work profile section in the memory management modal',
});

const workProfileDescription = defineMessage({
	id: 'ai-mate.rovo-memories.manage-memory-content.work-profile.description',
	defaultMessage:
		'Rovo leverages your recent activity across the Teamwork Graph to give you personalized experience.',
	description: 'Description for the work profile section in the memory management modal',
});

const seeWhatRovoKnows = defineMessage({
	id: 'ai-mate.rovo-memories.manage-memory-content.work-profile.see-what-rovo-knows',
	defaultMessage: 'See what Rovo knows about you',
	description: 'Button label to trigger a chat prompt revealing what Rovo knows about the user',
});

const seeMyActivity = defineMessage({
	id: 'ai-mate.rovo-memories.manage-memory-content.work-profile.see-my-activity',
	defaultMessage: 'See my activity',
	description: 'Button label to open the work profile screen showing recent activity',
});

type WorkProfileSectionProps = {
	onSeeWhatRovoKnows: () => void;
	onSeeMyActivity?: () => void;
};

export const WorkProfileSection = ({
	onSeeWhatRovoKnows,
	onSeeMyActivity,
}: WorkProfileSectionProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.container}>
			<Stack space="space.200">
				<Stack space="space.050">
					<Inline space="space.075" alignBlock="center">
						<Image src={workProfileIconUrl} alt="" />
						<Box xcss={styles.title}>{formatMessage(workProfileTitle)}</Box>
					</Inline>
					<div css={styles.description}>
						<Text size="small" color="color.text.subtle">
							{formatMessage(workProfileDescription)}
						</Text>
					</div>
				</Stack>
				<Inline space="space.100" alignBlock="center" spread="space-between">
					<Inline space="space.100">
						<Box xcss={styles.buttonsContainer}>
							<Button iconBefore={RovoChatIcon} onClick={onSeeWhatRovoKnows}>
								{formatMessage(seeWhatRovoKnows)}
							</Button>
						</Box>
						<Box xcss={styles.buttonsContainer}>
							<Button onClick={onSeeMyActivity}>{formatMessage(seeMyActivity)}</Button>
						</Box>
					</Inline>
					<Image src={teamWorkGraphIcons} alt="" />
				</Inline>
			</Stack>
		</Box>
	);
};
